import { useSettingsContext } from 'src/components/settings';
import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useEntityTracker } from 'src/hooks/use-entity-tracker';
import Box from '@mui/material/Box';

import { NotificationSendTimerSnackbar } from 'src/components/notification-send-timer-snackbar/notification-send-timer-snackbar';

import Header from './header';
import Main from './main';
import NavHorizontal from './nav-horizontal';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import { useNavData } from './config-navigation';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function DashboardLayout({ children }: Props) {
  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();
  const navData = useNavData();
  const hasNavData = navData.some((section) => section.items.length > 0);

  useEntityTracker();

  const isHorizontal = hasNavData && settings.themeLayout === 'horizontal';

  const isMini = hasNavData && settings.themeLayout === 'mini';

  const renderNavMini = <NavMini data={navData} />;

  const renderHorizontal = <NavHorizontal data={navData} />;

  const renderNavVertical = hasNavData ? <NavVertical data={navData} openNav={nav.value} onCloseNav={nav.onFalse} /> : null;

  if (isHorizontal) {
    return (
      <>
        <Header data={navData} hasNav={hasNavData} onOpenNav={nav.onTrue} />

        {lgUp ? renderHorizontal : renderNavVertical}

        <Main hasNav={hasNavData}>{children}</Main>

        <NotificationSendTimerSnackbar />
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header data={navData} hasNav={hasNavData} onOpenNav={nav.onTrue} />

        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}

          <Main hasNav={hasNavData}>{children}</Main>
        </Box>

        <NotificationSendTimerSnackbar />
      </>
    );
  }

  return (
    <>
      <Header data={navData} hasNav={hasNavData} onOpenNav={nav.onTrue} />

      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}

        <Main hasNav={hasNavData}>{children}</Main>
      </Box>

      <NotificationSendTimerSnackbar />
    </>
  );
}
